import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../App'
import { 
  User, 
  Link2, 
  Check, 
  X, 
  ExternalLink,
  Shield,
  Bell,
  Palette,
  RefreshCw
} from 'lucide-react'

export default function Settings() {
  const { user, refreshUser } = useAuth()
  const [whoopStatus, setWhoopStatus] = useState({ connected: false, loading: true })
  const [canvasStatus, setCanvasStatus] = useState({ connected: false, loading: true })
  const [syncing, setSyncing] = useState({ whoop: false, canvas: false })
  const [syncMessage, setSyncMessage] = useState('')

  useEffect(() => {
    checkIntegrations()
  }, [])

  async function checkIntegrations() {
    // Check Whoop
    try {
      const whoopRes = await fetch('/api/whoop/status')
      if (whoopRes.ok) {
        const data = await whoopRes.json()
        setWhoopStatus({ connected: data.connected, loading: false, profile: data.profile })
      }
    } catch (err) {
      setWhoopStatus({ connected: false, loading: false })
    }

    // Check Canvas
    try {
      const canvasRes = await fetch('/api/canvas/status')
      if (canvasRes.ok) {
        const data = await canvasRes.json()
        setCanvasStatus({ 
          connected: data.connected, 
          loading: false,
          user_name: data.user_name,
          base_url: data.base_url
        })
      }
    } catch (err) {
      setCanvasStatus({ connected: false, loading: false })
    }
  }

  async function connectWhoop() {
    const res = await fetch('/api/auth/whoop/authorize')
    if (res.ok) {
      const data = await res.json()
      window.location.href = data.url
    }
  }

  async function disconnectWhoop() {
    await fetch('/api/whoop/disconnect', { method: 'POST' })
    setWhoopStatus({ connected: false, loading: false })
  }

  async function syncCanvas() {
    setSyncing(s => ({ ...s, canvas: true }))
    setSyncMessage('')
    try {
      const res = await fetch('/api/canvas/sync', { method: 'POST' })
      const data = await res.json()
      if (res.ok) {
        setSyncMessage(`✓ ${data.message}`)
      } else {
        setSyncMessage(`✗ ${data.message}`)
      }
    } catch (err) {
      setSyncMessage('✗ Sync failed')
    } finally {
      setSyncing(s => ({ ...s, canvas: false }))
    }
  }

  return (
    <div className="page container" style={{ maxWidth: 800 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: 'var(--space-2xl)' }}
      >
        <h1 style={{ marginBottom: 'var(--space-xs)' }}>Settings</h1>
        <p className="text-secondary">Manage your account and integrations</p>
      </motion.div>

      {/* Profile Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-md)'
        }}>
          <User size={20} />
          Profile
        </h3>
        <div className="glass-card-static" style={{ padding: 'var(--space-xl)' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-lg)'
          }}>
            <div className="input-group">
              <label className="input-label">First Name</label>
              <input 
                type="text" 
                className="input" 
                defaultValue={user?.first_name || ''} 
                placeholder="First name"
              />
            </div>
            <div className="input-group">
              <label className="input-label">Last Name</label>
              <input 
                type="text" 
                className="input" 
                defaultValue={user?.last_name || ''} 
                placeholder="Last name"
              />
            </div>
            <div className="input-group" style={{ gridColumn: 'span 2' }}>
              <label className="input-label">Email</label>
              <input 
                type="email" 
                className="input" 
                defaultValue={user?.email || ''} 
                disabled
                style={{ opacity: 0.6 }}
              />
            </div>
          </div>
          <div style={{ marginTop: 'var(--space-lg)', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary">Save Changes</button>
          </div>
        </div>
      </motion.section>

      {/* Integrations Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-md)'
        }}>
          <Link2 size={20} />
          Integrations
        </h3>
        <div className="glass-card-static" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Whoop Integration */}
          <IntegrationItem
            name="Whoop"
            description="Sync your recovery, sleep, and strain data for personalized recommendations"
            connected={whoopStatus.connected}
            loading={whoopStatus.loading}
            onConnect={connectWhoop}
            onDisconnect={disconnectWhoop}
            logo={
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--radius-sm)',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.05em'
              }}>
                W
              </div>
            }
          />
          
          {/* Canvas Integration */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-lg)',
            padding: 'var(--space-xl)',
            borderBottom: '1px solid var(--glass-border)'
          }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--radius-sm)',
              background: '#E74C3C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1rem'
            }}>
              C
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <h4>Canvas LMS</h4>
                {canvasStatus.connected && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: '2px 8px',
                    background: 'var(--accent-green-muted)',
                    color: 'var(--accent-green)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}>
                    <Check size={12} />
                    Connected
                  </span>
                )}
              </div>
              <p className="text-secondary" style={{ fontSize: '0.875rem' }}>
                {canvasStatus.connected 
                  ? `Connected as ${canvasStatus.user_name || 'Babson Student'}` 
                  : 'Import assignments, deadlines, and grades from your school'}
              </p>
              {syncMessage && (
                <p style={{ 
                  fontSize: '0.75rem', 
                  marginTop: 'var(--space-xs)',
                  color: syncMessage.startsWith('✓') ? 'var(--accent-green)' : 'var(--accent-red)'
                }}>
                  {syncMessage}
                </p>
              )}
            </div>
            {canvasStatus.loading ? (
              <div className="animate-pulse text-tertiary">Loading...</div>
            ) : canvasStatus.connected ? (
              <button 
                className="btn btn-secondary" 
                onClick={syncCanvas}
                disabled={syncing.canvas}
              >
                <RefreshCw size={16} className={syncing.canvas ? 'animate-spin' : ''} />
                {syncing.canvas ? 'Syncing...' : 'Sync Now'}
              </button>
            ) : (
              <span className="text-tertiary" style={{ fontSize: '0.875rem' }}>Not configured</span>
            )}
          </div>
        </div>
      </motion.section>

      {/* Privacy Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-md)'
        }}>
          <Shield size={20} />
          Privacy & Data
        </h3>
        <div className="glass-card-static" style={{ padding: 'var(--space-xl)' }}>
          <p className="text-secondary" style={{ marginBottom: 'var(--space-md)' }}>
            Your data is encrypted and stored securely. We never share your personal information with third parties.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <a href="/privacy" className="btn btn-secondary">
              <ExternalLink size={16} />
              Privacy Policy
            </a>
            <button className="btn btn-ghost" style={{ color: 'var(--accent-red)' }}>
              Delete Account
            </button>
          </div>
        </div>
      </motion.section>
    </div>
  )
}

function IntegrationItem({ 
  name, 
  description, 
  connected, 
  loading, 
  onConnect, 
  onDisconnect, 
  logo,
  comingSoon 
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-lg)',
      padding: 'var(--space-xl)',
      borderBottom: '1px solid var(--glass-border)'
    }}>
      {logo}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <h4>{name}</h4>
          {connected && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
              padding: '2px 8px',
              background: 'var(--accent-green-muted)',
              color: 'var(--accent-green)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              <Check size={12} />
              Connected
            </span>
          )}
        </div>
        <p className="text-secondary" style={{ fontSize: '0.875rem' }}>
          {description}
        </p>
      </div>
      {loading ? (
        <div className="animate-pulse text-tertiary">Loading...</div>
      ) : connected ? (
        <button className="btn btn-ghost" onClick={onDisconnect}>
          <X size={16} />
          Disconnect
        </button>
      ) : comingSoon ? (
        <span className="text-tertiary" style={{ fontSize: '0.875rem' }}>Coming soon</span>
      ) : (
        <button className="btn btn-secondary" onClick={onConnect}>
          Connect
        </button>
      )}
    </div>
  )
}
